// =============================================================================
// Projekte — generic per-project P&L ledger. The user adds projects manually
// (e.g. "UV-Drucker"). Each project tracks its own expenses & income.
//
// Layout:
//   • List view: all projects with a tiny KPI strip (net profit, # entries)
//   • Detail view (selected project): headline profit card, cumulative-balance
//     area chart, top expense categories, filter pills, transaction list
// =============================================================================
import React, { useEffect, useMemo, useState } from 'react';
import {
  FolderKanban,
  Printer,
  Briefcase,
  Wrench,
  Palette,
  Camera,
  Code,
  Hammer,
  ChefHat,
  Plus,
  Trash2,
  Pencil,
  X,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Wallet,
  PartyPopper,
  ArrowDownRight,
  ArrowUpRight,
  Clock
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { formatDate as fmt } from '../lib/dates';
import { useToast } from '../lib/Toast';
import { useConfirm } from '../lib/ConfirmDialog';
import {
  listProjects,
  addProject,
  updateProject,
  deleteProject,
  listTransactions,
  listAllTransactions,
  addTransaction,
  updateTransaction,
  deleteTransaction,
  summarise,
  byCategory,
  cumulativeSeries,
  breakEvenDate,
  EXPENSE_CATEGORIES,
  INCOME_CATEGORIES
} from '../lib/projects';

// -----------------------------------------------------------------------------
// Formatting helpers.
// -----------------------------------------------------------------------------
const moneyFmt = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 2
});
const fmtMoney = (n) =>
  n == null || !Number.isFinite(n) ? '—' : moneyFmt.format(n);
const fmtMoneyShort = (n) => {
  if (n == null || !Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1).replace('.', ',')}k €`;
  return `${Math.round(n)} €`;
};
const fmtDate = (s) => {
  try {
    return fmt(parseISO(s), 'medium');
  } catch {
    return s;
  }
};
const fmtDateShort = (s) => {
  try {
    return fmt(parseISO(s), 'short');
  } catch {
    return s;
  }
};

// Tiny icon picker — keys map to lucide components.
const ICON_MAP = {
  folder: FolderKanban,
  printer: Printer,
  briefcase: Briefcase,
  wrench: Wrench,
  palette: Palette,
  camera: Camera,
  code: Code,
  hammer: Hammer,
  cooking: ChefHat
};
const ICON_KEYS = Object.keys(ICON_MAP);
const iconFor = (key) => ICON_MAP[key] || FolderKanban;

// =============================================================================
// Top-level: list or detail view.
// =============================================================================
const Projects = () => {
  const toast = useToast();
  const [projects, setProjects] = useState(null); // null = loading
  const [allTxns, setAllTxns] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [showAddProject, setShowAddProject] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  const reload = async () => {
    try {
      const [p, t] = await Promise.all([listProjects(), listAllTransactions()]);
      setProjects(p);
      setAllTxns(t);
    } catch (err) {
      toast.error(err.message || 'Laden fehlgeschlagen');
      setProjects([]);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = useMemo(
    () => (projects || []).find((p) => p.id === selectedId) || null,
    [projects, selectedId]
  );

  // Detail view ------------------------------------------------------------
  if (selected) {
    return (
      <ProjectDetail
        project={selected}
        onBack={() => setSelectedId(null)}
        onEditProject={() => setEditingProject(selected)}
        onAfterChange={reload}
        editingProject={editingProject}
        clearEditing={() => setEditingProject(null)}
      />
    );
  }

  // List view --------------------------------------------------------------
  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 pb-24 sm:pb-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-300">
          <FolderKanban size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            Projekte
          </h1>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Ausgaben &amp; Einnahmen pro Projekt
          </div>
        </div>
        <button
          onClick={() => setShowAddProject(true)}
          className="btn-primary flex items-center gap-1.5 text-sm"
        >
          <Plus size={16} />
          Neu
        </button>
      </div>

      {projects === null ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-sm text-gray-500 dark:text-gray-400">
          Lade…
        </div>
      ) : projects.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 p-8 text-center">
          <FolderKanban size={32} className="mx-auto text-gray-400 mb-3" />
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            Noch keine Projekte. Lege dein erstes Projekt an — z.B. „UV-Drucker".
          </p>
          <button
            onClick={() => setShowAddProject(true)}
            className="btn-primary inline-flex items-center gap-1.5 text-sm"
          >
            <Plus size={16} />
            Projekt anlegen
          </button>
        </div>
      ) : (
        <ul className="space-y-2">
          {projects.map((p) => {
            const txns = allTxns.filter((t) => t.projectId === p.id);
            const s = summarise(txns);
            return (
              <ProjectCard
                key={p.id}
                project={p}
                summary={s}
                onOpen={() => setSelectedId(p.id)}
                onEdit={() => setEditingProject(p)}
              />
            );
          })}
        </ul>
      )}

      {showAddProject && (
        <ProjectFormModal
          onClose={() => setShowAddProject(false)}
          onSaved={async () => {
            setShowAddProject(false);
            await reload();
            toast.success('Projekt angelegt');
          }}
        />
      )}
      {editingProject && !selected && (
        <ProjectFormModal
          existing={editingProject}
          onClose={() => setEditingProject(null)}
          onSaved={async () => {
            setEditingProject(null);
            await reload();
            toast.success('Aktualisiert');
          }}
          onDeleted={async () => {
            setEditingProject(null);
            await reload();
            toast.success('Projekt gelöscht');
          }}
        />
      )}
    </div>
  );
};

export default Projects;

// =============================================================================
// ProjectCard — list-view row.
// =============================================================================
const ProjectCard = ({ project, summary, onOpen, onEdit }) => {
  const Icon = iconFor(project.icon);
  const isTime = project.kind === 'time';
  const { netProfit, isProfitable, count } = summary;
  const inLoss = !isTime && netProfit < 0;
  return (
    <li>
      <button
        onClick={onOpen}
        className="w-full text-left rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 hover:border-primary-300 dark:hover:border-primary-700 transition-colors flex items-center gap-3"
      >
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
            isTime
              ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
              : isProfitable
              ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
              : inLoss
              ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
          }`}
        >
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <div className="font-medium text-gray-900 dark:text-gray-100 truncate flex items-center gap-1.5">
              {project.name}
              {isTime && (
                <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                  <Clock size={10} />
                  Zeit
                </span>
              )}
            </div>
            {!isTime && (
              <div
                className={`text-sm font-semibold tabular-nums flex-shrink-0 ${
                  isProfitable
                    ? 'text-emerald-700 dark:text-emerald-300'
                    : inLoss
                    ? 'text-amber-700 dark:text-amber-300'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                {netProfit > 0 ? '+' : ''}
                {fmtMoney(netProfit)}
              </div>
            )}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {isTime
              ? 'Reines Zeit-Projekt'
              : count === 0
              ? 'Noch keine Einträge'
              : `${count} ${count === 1 ? 'Eintrag' : 'Einträge'}`}
          </div>
        </div>
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onEdit();
            }
          }}
          className="p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 flex-shrink-0 cursor-pointer"
          aria-label="Projekt bearbeiten"
        >
          <Pencil size={14} />
        </span>
        <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
      </button>
    </li>
  );
};

// =============================================================================
// ProjectDetail — the original ledger UI, scoped to one project.
// =============================================================================
const ProjectDetail = ({
  project,
  onBack,
  onEditProject,
  onAfterChange,
  editingProject,
  clearEditing
}) => {
  const toast = useToast();
  const confirm = useConfirm();

  const [txns, setTxns] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showAdd, setShowAdd] = useState(null);
  const [editing, setEditing] = useState(null);

  const reload = async () => {
    try {
      const data = await listTransactions(project.id);
      setTxns(data);
    } catch (err) {
      toast.error(err.message || 'Laden fehlgeschlagen');
      setTxns([]);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  const summary = useMemo(() => summarise(txns || []), [txns]);
  const series = useMemo(() => cumulativeSeries(txns || []), [txns]);
  const beDate = useMemo(() => breakEvenDate(txns || []), [txns]);
  const topExpenseCats = useMemo(
    () => byCategory(txns || [], 'expense').slice(0, 5),
    [txns]
  );

  const filteredTxns = useMemo(() => {
    if (!txns) return [];
    if (filter === 'all') return txns;
    return txns.filter((t) => t.kind === filter);
  }, [txns, filter]);

  const handleDelete = async (t) => {
    const ok = await confirm({
      title: t.kind === 'expense' ? 'Ausgabe löschen?' : 'Einnahme löschen?',
      message: `${fmtDate(t.txnDate)} · ${fmtMoney(t.amount)} – wirklich löschen?`,
      confirmLabel: 'Löschen',
      destructive: true
    });
    if (!ok) return;
    try {
      await deleteTransaction(t.id);
      await reload();
      onAfterChange?.();
    } catch (err) {
      toast.error(err.message || 'Löschen fehlgeschlagen');
    }
  };

  const Icon = iconFor(project.icon);

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 pb-24 sm:pb-6">
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={onBack}
          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300"
          aria-label="Zurück"
        >
          <ChevronLeft size={20} />
        </button>
        <div className="w-10 h-10 rounded-full bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-300">
          <Icon size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 truncate">
            {project.name}
          </h1>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Ausgaben &amp; Einnahmen
          </div>
        </div>
        <button
          onClick={onEditProject}
          className="p-1.5 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
          aria-label="Projekt bearbeiten"
        >
          <Pencil size={16} />
        </button>
      </div>

      {project.kind === 'time' ? (
        <div className="rounded-xl border border-blue-200 dark:border-blue-900/50 bg-blue-50 dark:bg-blue-900/20 p-4 mb-4 text-sm text-gray-700 dark:text-gray-200 flex items-start gap-2">
          <Clock size={16} className="text-blue-600 dark:text-blue-300 mt-0.5 flex-shrink-0" />
          <div>
            Reines Zeit-Projekt. Erfasse Zeit über den <strong>Time Tracker</strong>; Ausgaben &amp; Einnahmen werden hier nicht erfasst.
          </div>
        </div>
      ) : (
        <>
          <ProfitCard summary={summary} breakEvenAt={beDate} />
          {series.length >= 2 && (
            <BalanceChart series={series} net={summary.netProfit} />
          )}
        </>
      )}

      {project.kind !== 'time' && topExpenseCats.length > 0 && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 mb-4">
          <div className="text-xs uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400 mb-3">
            Größte Ausgaben
          </div>
          <ul className="space-y-2">
            {topExpenseCats.map((c) => {
              const pct =
                summary.totalExpense > 0
                  ? (c.total / summary.totalExpense) * 100
                  : 0;
              return (
                <li key={c.category}>
                  <div className="flex items-baseline justify-between text-sm mb-1">
                    <span className="text-gray-700 dark:text-gray-300">
                      {c.category}
                    </span>
                    <span className="font-medium tabular-nums text-gray-900 dark:text-gray-100">
                      {fmtMoney(c.total)}
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-amber-500 dark:bg-amber-600"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {project.kind !== 'time' && (
        <div className="grid grid-cols-2 gap-2 mb-4">
          <button
            onClick={() => setShowAdd('expense')}
            className="btn bg-amber-600 text-white hover:bg-amber-700 flex items-center justify-center gap-2"
          >
            <ArrowDownRight size={16} />
            Ausgabe
          </button>
          <button
            onClick={() => setShowAdd('income')}
            className="btn bg-emerald-600 text-white hover:bg-emerald-700 flex items-center justify-center gap-2"
          >
            <ArrowUpRight size={16} />
            Einnahme
          </button>
        </div>
      )}

      {project.kind !== 'time' && txns && txns.length > 0 && (
        <div className="flex gap-1.5 mb-3">
          <FilterPill active={filter === 'all'} onClick={() => setFilter('all')}>
            Alle ({txns.length})
          </FilterPill>
          <FilterPill
            active={filter === 'expense'}
            onClick={() => setFilter('expense')}
          >
            Ausgaben
          </FilterPill>
          <FilterPill
            active={filter === 'income'}
            onClick={() => setFilter('income')}
          >
            Einnahmen
          </FilterPill>
        </div>
      )}

      {project.kind === 'time' ? null : txns === null ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-sm text-gray-500 dark:text-gray-400">
          Lade…
        </div>
      ) : filteredTxns.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 p-6 text-center">
          <Icon size={28} className="mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {txns.length === 0
              ? 'Noch keine Einträge. Lege deine erste Ausgabe oder Einnahme an.'
              : 'Keine Einträge in dieser Kategorie.'}
          </p>
        </div>
      ) : (
        <ul className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800 overflow-hidden">
          {filteredTxns.map((t) => (
            <TxnRow
              key={t.id}
              txn={t}
              onEdit={() => setEditing(t)}
              onDelete={() => handleDelete(t)}
            />
          ))}
        </ul>
      )}

      {showAdd && (
        <TxnFormModal
          projectId={project.id}
          kind={showAdd}
          onClose={() => setShowAdd(null)}
          onSaved={async () => {
            setShowAdd(null);
            await reload();
            onAfterChange?.();
            toast.success('Gespeichert');
          }}
        />
      )}
      {editing && (
        <TxnFormModal
          projectId={project.id}
          kind={editing.kind}
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
            onAfterChange?.();
            toast.success('Aktualisiert');
          }}
        />
      )}
      {editingProject && (
        <ProjectFormModal
          existing={editingProject}
          onClose={clearEditing}
          onSaved={async () => {
            clearEditing();
            await onAfterChange?.();
            toast.success('Aktualisiert');
          }}
          onDeleted={async () => {
            clearEditing();
            onBack();
            await onAfterChange?.();
            toast.success('Projekt gelöscht');
          }}
        />
      )}
    </div>
  );
};

// =============================================================================
// ProfitCard
// =============================================================================
const ProfitCard = ({ summary, breakEvenAt }) => {
  const { totalExpense, totalIncome, netProfit, isProfitable, isBreakEven } =
    summary;
  const inLoss = netProfit < 0;
  const remainingToBE = inLoss ? -netProfit : 0;

  return (
    <div
      className={`rounded-xl border p-4 mb-3 ${
        isProfitable
          ? 'border-emerald-200 dark:border-emerald-900/50 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-gray-900'
          : inLoss
          ? 'border-amber-200 dark:border-amber-900/50 bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/20 dark:to-gray-900'
          : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900'
      }`}
    >
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-xs uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
          <Wallet size={12} />
          Bilanz
        </div>
        {isProfitable ? (
          <div className="flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
            <TrendingUp size={12} />
            Gewinn
          </div>
        ) : inLoss ? (
          <div className="flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-300">
            <TrendingDown size={12} />
            noch im Minus
          </div>
        ) : null}
      </div>
      <div
        className={`text-3xl font-bold tabular-nums ${
          isProfitable
            ? 'text-emerald-700 dark:text-emerald-300'
            : inLoss
            ? 'text-amber-700 dark:text-amber-300'
            : 'text-gray-900 dark:text-gray-100'
        }`}
      >
        {netProfit > 0 ? '+' : ''}
        {fmtMoney(netProfit)}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Einnahmen
          </div>
          <div className="text-sm font-semibold text-emerald-700 dark:text-emerald-300 tabular-nums">
            {fmtMoney(totalIncome)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Ausgaben
          </div>
          <div className="text-sm font-semibold text-amber-700 dark:text-amber-300 tabular-nums">
            {fmtMoney(totalExpense)}
          </div>
        </div>
      </div>
      {isBreakEven && breakEvenAt ? (
        <div className="mt-3 pt-3 border-t border-emerald-200/50 dark:border-emerald-900/40 flex items-center gap-2 text-xs">
          <PartyPopper size={14} className="text-emerald-600 dark:text-emerald-400" />
          <span className="text-gray-700 dark:text-gray-200">
            Break-even erreicht am{' '}
            <span className="font-semibold">{fmtDate(breakEvenAt)}</span>
          </span>
        </div>
      ) : inLoss && totalIncome > 0 ? (
        <div className="mt-3 pt-3 border-t border-amber-200/50 dark:border-amber-900/40 text-xs text-gray-600 dark:text-gray-300">
          Noch{' '}
          <span className="font-semibold tabular-nums">
            {fmtMoney(remainingToBE)}
          </span>{' '}
          bis zum Break-even.
        </div>
      ) : null}
    </div>
  );
};

// =============================================================================
// BalanceChart
// =============================================================================
const BalanceChart = ({ series, net }) => {
  const data = series.map((p) => ({
    date: p.date,
    balance: p.balance,
    label: fmtDateShort(p.date)
  }));
  const isPositive = net >= 0;
  const stroke = isPositive ? '#059669' : '#d97706';
  const fillId = isPositive ? 'projFillPos' : 'projFillNeg';

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 mb-4">
      <div className="text-xs uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400 mb-2 px-1">
        Verlauf
      </div>
      <div style={{ width: '100%', height: 180 }}>
        <ResponsiveContainer>
          <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="projFillPos" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="projFillNeg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: '#9ca3af' }}
              axisLine={false}
              tickLine={false}
              minTickGap={20}
            />
            <YAxis
              tick={{ fontSize: 10, fill: '#9ca3af' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={fmtMoneyShort}
              width={50}
            />
            <Tooltip
              contentStyle={{
                fontSize: 12,
                borderRadius: 8,
                border: '1px solid #e5e7eb'
              }}
              labelFormatter={(_, payload) =>
                payload && payload[0] ? fmtDate(payload[0].payload.date) : ''
              }
              formatter={(value) => [fmtMoney(value), 'Bilanz']}
            />
            <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="3 3" />
            <Area
              type="monotone"
              dataKey="balance"
              stroke={stroke}
              strokeWidth={2}
              fill={`url(#${fillId})`}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// =============================================================================
// Filter pill + transaction row.
// =============================================================================
const FilterPill = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
      active
        ? 'bg-primary-600 border-primary-600 text-white'
        : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300'
    }`}
  >
    {children}
  </button>
);

const TxnRow = ({ txn: t, onEdit, onDelete }) => {
  const isExpense = t.kind === 'expense';
  return (
    <li className="flex items-center gap-2 p-3">
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isExpense
            ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
            : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
        }`}
      >
        {isExpense ? <ArrowDownRight size={14} /> : <ArrowUpRight size={14} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
            {t.category || (isExpense ? 'Ausgabe' : 'Einnahme')}
          </div>
          <div
            className={`text-sm font-semibold tabular-nums flex-shrink-0 ${
              isExpense
                ? 'text-amber-700 dark:text-amber-300'
                : 'text-emerald-700 dark:text-emerald-300'
            }`}
          >
            {isExpense ? '−' : '+'}
            {fmtMoney(t.amount)}
          </div>
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
          {fmtDate(t.txnDate)}
          {t.note && ` · ${t.note}`}
        </div>
      </div>
      <button
        onClick={onEdit}
        className="p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 flex-shrink-0"
        aria-label="Bearbeiten"
      >
        <Pencil size={14} />
      </button>
      <button
        onClick={onDelete}
        className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 flex-shrink-0"
        aria-label="Löschen"
      >
        <Trash2 size={14} />
      </button>
    </li>
  );
};

// =============================================================================
// TxnFormModal — add/edit a transaction for a given project.
// =============================================================================
const TxnFormModal = ({ projectId, kind, existing, onClose, onSaved }) => {
  const toast = useToast();
  const today = format(new Date(), 'yyyy-MM-dd');
  const [date, setDate] = useState(existing?.txnDate || today);
  const [amount, setAmount] = useState(existing ? String(existing.amount) : '');
  const [category, setCategory] = useState(existing?.category || '');
  const [note, setNote] = useState(existing?.note || '');
  const [saving, setSaving] = useState(false);

  const presets = kind === 'expense' ? EXPENSE_CATEGORIES : INCOME_CATEGORIES;
  const isExpense = kind === 'expense';
  const amt = Number(amount);
  const valid = amount !== '' && Number.isFinite(amt) && amt > 0 && date;

  const handleSave = async () => {
    if (saving || !valid) return;
    setSaving(true);
    try {
      if (existing) {
        await updateTransaction(existing.id, {
          txnDate: date,
          amount: amt,
          category,
          note
        });
      } else {
        await addTransaction({
          projectId,
          txnDate: date,
          kind,
          amount: amt,
          category,
          note
        });
      }
      onSaved();
    } catch (err) {
      toast.error(err.message || 'Speichern fehlgeschlagen');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-md p-5 max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                isExpense
                  ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                  : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
              }`}
            >
              {isExpense ? (
                <ArrowDownRight size={16} />
              ) : (
                <ArrowUpRight size={16} />
              )}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {existing
                ? isExpense
                  ? 'Ausgabe bearbeiten'
                  : 'Einnahme bearbeiten'
                : isExpense
                ? 'Neue Ausgabe'
                : 'Neue Einnahme'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Datum
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="input w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Betrag (€)
            </label>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={isExpense ? 'z.B. 150' : 'z.B. 80'}
              className="input w-full"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Kategorie
            </label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {presets.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setCategory(p)}
                  className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${
                    category === p
                      ? isExpense
                        ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                        : 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="oder eigene Kategorie"
              className="input w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Notiz (optional)
            </label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={
                isExpense
                  ? 'z.B. Tinte Cyan nachbestellt'
                  : 'z.B. Auftrag Lisa – 5 Tassen'
              }
              className="input w-full"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={handleSave}
              disabled={saving || !valid}
              className={`btn text-white flex-1 disabled:opacity-50 disabled:cursor-not-allowed ${
                isExpense
                  ? 'bg-amber-600 hover:bg-amber-700'
                  : 'bg-emerald-600 hover:bg-emerald-700'
              }`}
            >
              {saving ? 'Speichere…' : 'Speichern'}
            </button>
            <button onClick={onClose} className="btn-secondary">
              Abbrechen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// ProjectFormModal — add/edit a project (name + icon).
// =============================================================================
const ProjectFormModal = ({ existing, onClose, onSaved, onDeleted }) => {
  const toast = useToast();
  const confirm = useConfirm();
  const [name, setName] = useState(existing?.name || '');
  const [icon, setIcon] = useState(existing?.icon || 'folder');
  const [kind, setKind] = useState(existing?.kind || 'profit');
  const [saving, setSaving] = useState(false);

  const valid = name.trim().length > 0;

  const handleSave = async () => {
    if (saving || !valid) return;
    setSaving(true);
    try {
      if (existing) {
        await updateProject(existing.id, { name, icon, kind });
      } else {
        await addProject({ name, icon, kind });
      }
      onSaved();
    } catch (err) {
      toast.error(err.message || 'Speichern fehlgeschlagen');
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existing) return;
    const ok = await confirm({
      title: 'Projekt löschen?',
      message: `„${existing.name}" und alle zugehörigen Einträge werden unwiderruflich gelöscht.`,
      confirmLabel: 'Löschen',
      destructive: true
    });
    if (!ok) return;
    try {
      await deleteProject(existing.id);
      onDeleted?.();
    } catch (err) {
      toast.error(err.message || 'Löschen fehlgeschlagen');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-md p-5 max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {existing ? 'Projekt bearbeiten' : 'Neues Projekt'}
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. UV-Drucker"
              className="input w-full"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Art
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setKind('profit')}
                className={`p-3 rounded-lg border text-left transition-colors ${
                  kind === 'profit'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                  <Wallet size={14} />
                  Gewinn &amp; Zeit
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Ausgaben/Einnahmen + Zeit
                </div>
              </button>
              <button
                type="button"
                onClick={() => setKind('time')}
                className={`p-3 rounded-lg border text-left transition-colors ${
                  kind === 'time'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                  <Clock size={14} />
                  Nur Zeit
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Nur Zeit erfassen
                </div>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Symbol
            </label>
            <div className="flex flex-wrap gap-1.5">
              {ICON_KEYS.map((k) => {
                const I = ICON_MAP[k];
                const active = icon === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setIcon(k)}
                    className={`w-10 h-10 rounded-lg border flex items-center justify-center transition-colors ${
                      active
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300'
                    }`}
                    aria-label={k}
                  >
                    <I size={18} />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={handleSave}
              disabled={saving || !valid}
              className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Speichere…' : 'Speichern'}
            </button>
            <button onClick={onClose} className="btn-secondary">
              Abbrechen
            </button>
          </div>
          {existing && (
            <button
              onClick={handleDelete}
              className="w-full text-sm text-red-600 dark:text-red-400 hover:underline pt-2"
            >
              Projekt löschen
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
